import React, { Suspense, startTransition } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

// Query client
import { queryClient } from '@/lib/queryClient';

// Layout Components
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Common Components
import LazyWrapper from '@/components/common/LazyWrapper';
import SafeLazyWrapper from '@/components/common/SafeLazyWrapper';


// Core Pages (not lazy loaded for immediate access)
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';

// Lazy loaded page components for better performance
const ProfessionalPOSInterface = React.lazy(() => import('@/features/pos/pages/POSInterface').then(module => ({ default: module.POSInterface })));
const OrdersPage = React.lazy(() => import('@/features/pages/PlaceholderPages').then(module => ({ default: module.OrdersPage })));
const MenuPage = React.lazy(() => import('@/features/pages/PlaceholderPages').then(module => ({ default: module.MenuPage })));
// The owner's real analytics screen; the placeholder it replaced is still
// used by other routes in that module.
const AnalyticsPage = React.lazy(() => import('@/features/admin/pages/OwnerAnalyticsPage').then(module => ({ default: module.OwnerAnalyticsPage })));
const NotFoundPage = React.lazy(() => import('@/features/pages/PlaceholderPages').then(module => ({ default: module.NotFoundPage })));
const EmployeesPage = React.lazy(() => import('@/features/employees/pages/EmployeesPage'));
const ManagerDashboard = React.lazy(() => import('@/features/admin/pages/ManagerDashboard'));
const DeveloperToolsPage = React.lazy(() => import('@/features/developer/pages/DeveloperToolsPage'));

// Manager role-specific pages
const ManagerTablesPage = React.lazy(() => import('@/features/manager/pages/ManagerTablesPage'));
const ManagerOrdersPage = React.lazy(() => import('@/features/manager/pages/ManagerOrdersPage'));
const ManagerKitchenPage = React.lazy(() => import('@/features/manager/pages/ManagerKitchenPage'));
const ManagerMenuManagementPage = React.lazy(() => import('@/features/menu/pages/ManagerMenuManagementPage'));

// Chef role-specific pages
const ChefKitchenPage = React.lazy(() => import('@/features/chef/pages/ChefKitchenPage'));
const ChefMenuViewPage = React.lazy(() => import('@/features/menu/pages/ChefMenuViewPage'));

// Admin role-specific pages
const AdminKitchenMonitorPage = React.lazy(() => import('@/features/admin/pages/AdminKitchenMonitorPage'));
const PricingManagementPage = React.lazy(() => import('@/features/admin/pages/PricingManagementPage'));
const RestaurantSettingsPage = React.lazy(() => import('@/features/admin/pages/RestaurantSettingsPage'));

// Waiter role-specific pages
const WaiterTablesPage = React.lazy(() => import('@/features/waiter/pages/WaiterTablesPage'));

// Design exploration — dev only, unauthenticated, isolated styles (see /design-preview).
// The route below is gated on import.meta.env.DEV, so in production /design-preview
// falls through to the 404 handler. Rollup still emits the lazy chunk (dynamic
// imports become chunks during graph construction), but it is never requested.
const DesignPreviewPage = React.lazy(() => import('@/features/design-preview/DesignPreviewPage'));

// Platform Management Components (Super Admin)
const RestaurantManagementPage = React.lazy(() => import('@/features/admin/pages/RestaurantManagementPage').then(module => ({ default: module.RestaurantManagementPage })));
const UserManagementPage = React.lazy(() => import('@/features/admin/pages/UserManagement').then(module => ({ default: module.default })));
const PlatformAnalyticsPage = React.lazy(() => import('@/features/admin/pages/PlatformAnalytics').then(module => ({ default: module.default })));
const PlatformSettingsPage = React.lazy(() => import('@/features/admin/pages/PlatformSettings').then(module => ({ default: module.default })));

// Hooks & Stores
import { useAuthStore, initDevelopmentMode } from '@/stores/authStore';
import { useRestaurantStore } from '@/stores/restaurantStore';
import { useAutoLogin } from '@/hooks/useAuth';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

// Loading Component
const LoadingScreen = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-neutral-600 font-medium">{t('common.loading', 'Загрузка Nova Cafe...')}</p>
      </motion.div>
    </div>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { user, isLoading } = useAuthStore();
  const [isNavigating, setIsNavigating] = React.useState(false);

  React.useEffect(() => {
    if (isLoading) {
      setIsNavigating(true);
    } else {
      startTransition(() => {
        setIsNavigating(false);
      });
    }
  }, [isLoading]);

  if (isLoading || isNavigating) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      {children}
    </Suspense>
  );
};

// Page transition animations
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.4,
};

// App Content Component (inside providers)
const AppContent = () => {
  const { t } = useSafeTranslation();
  const { isLoading, user } = useAuthStore();
  const { loadRestaurant } = useRestaurantStore();

  // Auto-login hook handles authentication initialization
  useAutoLogin();

  // Initialize restaurant data and performance monitoring
  React.useEffect(() => {
    console.log('Nova Cafe App initialized');

    // Initialize development mode auto-login
    initDevelopmentMode();

    // Load restaurant data (only if user is authenticated and not Super Admin)
    startTransition(() => {
      // Only load restaurant data if:
      // 1. User is authenticated
      // 2. User is not Super Admin (they operate at platform level)
      if (user && user.role !== 'super_admin') {
        loadRestaurant();
      }
    });
  }, [loadRestaurant, user?.role, user]);


  if (isLoading) {
    return <LoadingScreen />;
  }

  // Waiters also get the dashboard layout with sidebar, but use POS interface
  // No special waiter-only layout needed anymore

  // Managers get the full dashboard layout with sidebar (but no POS access)

  return (
    <div className="min-h-screen bg-neutral-50">
      <Suspense fallback={<LoadingScreen />}>
        <AnimatePresence mode="wait">
          <Routes>
          {/* Authentication Routes */}
          <Route
            path="/login"
            element={
              <AuthLayout>
                <motion.div
                  key="login"
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <LoginPage />
                </motion.div>
              </AuthLayout>
            }
          />

          {/* Design exploration — dev builds only, no auth, no app chrome */}
          {import.meta.env.DEV && (
            <Route
              path="/design-preview"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <DesignPreviewPage />
                </Suspense>
              }
            />
          )}

          {/* Protected Dashboard Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard Home - Redirect based on role */}
            <Route
              index
              element={
                user?.role === 'super_admin' ? (
                  <motion.div
                    key="dashboard"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <DashboardPage />
                  </motion.div>
                ) : user?.role === 'manager' ? (
                  <Navigate to="/manager" replace />
                ) : user?.role === 'waiter' ? (
                  <Navigate to="/pos" replace />
                ) : user?.role === 'developer' ? (
                  <Navigate to="/developer" replace />
                ) : (
                  <motion.div
                    key="dashboard"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <DashboardPage />
                  </motion.div>
                )
              }
            />

            {/* Manager Dashboard */}
            <Route
              path="manager"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <SafeLazyWrapper errorMessage={t('app.loadFailedManagerDashboard')}>
                      <ManagerDashboard />
                    </SafeLazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Tables Management */}

            {/* Orders Management */}

            {/* Menu Management */}
            <Route
              path="menu"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager', 'chef']}>
                  <motion.div
                    key="menu"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <MenuPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* POS Interface - For waiters, admin and servers, NOT managers */}
            <Route
              path="pos"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager', 'server', 'waiter']}>
                  <motion.div
                    key="pos"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ProfessionalPOSInterface />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Kitchen Display */}
            <Route
              path="kitchen"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager', 'chef']}>
                  <motion.div
                    key="kitchen"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ChefKitchenPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Analytics */}

            {/* Employees Management */}
            <Route
              path="employees"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="employees"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <EmployeesPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Staff lives at /employees; these two routes rendered the same
                component under different names. */}
            <Route path="manager/staff" element={<Navigate to="/employees" replace />} />
            <Route path="waiter/profile" element={<Navigate to="/employees" replace />} />

            {/* Developer Tools */}
            <Route
              path="developer"
              element={
                <ProtectedRoute roles={['developer']}>
                  <motion.div
                    key="developer"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <DeveloperToolsPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Settings */}

            {/* Role-based Routes */}
            
            {/* Admin Routes */}
            <Route
              path="admin/kitchen-monitor"
              element={
                <ProtectedRoute roles={['restaurant_owner']}>
                  <motion.div
                    key="admin-kitchen-monitor"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <AdminKitchenMonitorPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/analytics"
              element={
                <ProtectedRoute roles={['restaurant_owner']}>
                  <motion.div
                    key="admin-analytics"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <AnalyticsPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/settings"
              element={
                <ProtectedRoute roles={['restaurant_owner']}>
                  <motion.div
                    key="admin-settings"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <RestaurantSettingsPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/pricing"
              element={
                <ProtectedRoute roles={['restaurant_owner']}>
                  <motion.div
                    key="admin-pricing"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <PricingManagementPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Manager Routes */}
            <Route
              path="manager/tables"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager-tables"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ManagerTablesPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="manager/orders"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager-orders"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ManagerOrdersPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="manager/menu"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager-menu"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ManagerMenuManagementPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="manager/kitchen"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager-kitchen"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ManagerKitchenPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="manager/analytics"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager']}>
                  <motion.div
                    key="manager-analytics"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <AnalyticsPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Chef Routes */}
            <Route
              path="chef/kitchen"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'chef']}>
                  <motion.div
                    key="chef-kitchen"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ChefKitchenPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="chef/menu"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'chef']}>
                  <motion.div
                    key="chef-menu"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ChefMenuViewPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Waiter Routes */}
            <Route
              path="waiter/pos"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager', 'waiter']}>
                  <motion.div
                    key="waiter-pos"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <ProfessionalPOSInterface />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="waiter/tables"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'manager', 'waiter']}>
                  <motion.div
                    key="waiter-tables"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <WaiterTablesPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="waiter/orders"
              element={
                <ProtectedRoute roles={['restaurant_owner', 'waiter']}>
                  <motion.div
                    key="waiter-orders"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <OrdersPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* Platform Management Routes (Super Admin) */}
            <Route
              path="platform/restaurants"
              element={
                <ProtectedRoute roles={['super_admin']}>
                  <motion.div
                    key="platform-restaurants"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <RestaurantManagementPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            <Route
              path="platform/users"
              element={
                <ProtectedRoute roles={['super_admin']}>
                  <motion.div
                    key="platform-users"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <UserManagementPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            <Route
              path="platform/analytics"
              element={
                <ProtectedRoute roles={['super_admin']}>
                  <motion.div
                    key="platform-analytics"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <PlatformAnalyticsPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

            <Route
              path="platform/settings"
              element={
                <ProtectedRoute roles={['super_admin']}>
                  <motion.div
                    key="platform-settings"
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <LazyWrapper>
                      <PlatformSettingsPage />
                    </LazyWrapper>
                  </motion.div>
                </ProtectedRoute>
              }
            />

          </Route>

          {/* 404 Not Found */}
          <Route
            path="*"
            element={
              <motion.div
                key="not-found"
                initial="initial"
                animate="in"
                exit="out"
                variants={pageVariants}
                transition={pageTransition}
              >
                <NotFoundPage />
              </motion.div>
            }
          />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </div>
  );
};

// Professional App Component with Providers
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#0a0a0a',
            color: '#fff',
          },
          success: {
            style: {
              background: '#027a48',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#027a48',
            },
          },
          error: {
            style: {
              background: '#b42318',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#b42318',
            },
          },
        }}
      />
      
      {/* React Query DevTools (only in development) */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}

export default App;